import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {IData} from '../../interfaces/IData';
import {IVideoList} from '../../interfaces/IVideoList';

import './ListVideo.scss';

type ListVideoProps = {
    content: IData | undefined;
}

const isRecent = (ago: string | undefined) =>
    /hour|minute|second|now|just/i.test(ago || '');

const ListVideo = ({content}: ListVideoProps) => {
    // Number of videos in chunk
    const numVideos = 30;
    // Initial page (chunk)
    const [page, setPage] = useState(1);
    // Videos list from chunks
    const [items, setItems] = useState<IVideoList[]>();
    // Reference on 'Loading...' element
    const [bottom, setBottom] = useState<HTMLDivElement | null>(null);
    // User was intersected an element and chunk will be generated
    const [isIntersecting, setIntersecting] = useState(false);
    // Will hold the Intersection Observer
    const bottomObserver = useRef<IntersectionObserver>();

    useEffect(() => {
        // Initial videos list
        if (content && content['video-list']) {
            setItems(content['video-list'].slice(0, numVideos));
        }
    }, [content]);

    useEffect(() => {
        // Chunk generating
        if (content && content['video-list'] && isIntersecting) lazyVideosFetch();
    });

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                // User scrolled to last element
                if (entry.isIntersecting) {
                    setIntersecting(true);
                }
            },
            {threshold: 0.25, rootMargin: '200px'}
        );

        bottomObserver.current = observer;

        if (bottom) {
            observer.observe(bottom);
        }

        return () => {
            if (bottom) {
                observer.unobserve(bottom);
            }
        };
    }, [bottom]);

    /**
     * Generating chunk
     */
    const lazyVideosFetch = () => {
        setPage(page + 1);
        if (content && content['video-list']) {
            setItems(content['video-list'].slice(0, numVideos * page));
        }
        setIntersecting(false);
    };

    return (
        <>
            <section className="hero">
                <h1>Latest <span className="grad">scripts &amp; exploits</span></h1>
                <p>Fresh working scripts, GUIs and auto-farms — updated daily.</p>
            </section>

            <section className="video-grid animate__animated animate__fadeInUp">
                {items &&
                    items.map((value) => (
                        <article key={value['embed'] + value['title']} className="vcard">
                            <Link className="thumb" to={{pathname: `/v/${value['video_id']}`}}>
                                <div
                                    className="thumb-img"
                                    style={{backgroundImage: 'url("' + value['thumbnail'] + '")'}}
                                />
                                <div className="thumb-glow"/>
                                {isRecent(value['ago']) && <span className="badge-new">New</span>}
                                <span className="play">
                                    <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                </span>
                            </Link>

                            <div className="vbody">
                                <Link className="vtitle" to={{pathname: `/v/${value['video_id']}`}}>
                                    {value['title']}
                                </Link>
                                <div className="vmeta">
                                    <span>{value['views']}</span>
                                    {value['ago'] && <><span className="dot"/><span>{value['ago']}</span></>}
                                </div>
                                <Link className="cta" to={{pathname: `/v/${value['video_id']}`}}>
                                    <svg viewBox="0 0 24 24"><path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"/></svg>
                                    Get script
                                </Link>
                            </div>
                        </article>
                    ))}
                <div ref={setBottom} className="loader-lazy-load"/>
            </section>
        </>
    );
};

export default ListVideo;
